import ProductBadge from './ProductBadge';
import ProductReviews from './ProductReviews';

const currency_symbol = window.theme.currency_symbol ? window.theme.currency_symbol : '$';
const currency_iso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';
const yotpo_key =
  window.location.hostname === 'www.wndsr.dev'
    ? '41yw0gmFPCJm3ErG8NjBZAEKbxG3ptjvaYbMJTUB'
    : '5t2myNOMLouyXZtIpwsjsbejgY0mJ4kdf3QJ8JAE';

export class ProductTile {
  #minimumAvailablePrice = 0;
  #maximumAvailablePrice = 0;
  #imageWidth = 500;
  #imageHeight = 750;

  static currencySymbol = currency_symbol;
  static currencyIso = currency_iso;
  static yotpoKey = yotpo_key;

  /**
   *
   * @param {Object} builder - This will come from a builder
   */
  constructor(builder) {
    this.productTileData = {
      pid: builder.pid || null, // Product ID
      title: builder.title || null,
      productUrl: builder.productUrl || null,
      productType: builder.productType || null,
      imageUrl: builder.imageUrl || null,
      imageAlt: builder.title || null,
      price: builder.price || null,
      compareAtPrice: builder.compareAtPrice || null,
      maxCompareAtPrice: builder.maxCompareAtPrice || null,
      tags: builder.tags || null,
      variants: builder.variants || null,
      options: builder.options || null,
      isRec: builder.isRec || null,
      shouldReportClickToAdmind: builder.shouldReportClickToAdmind || null,
      selectedColor: builder.selectedColor || null,
      recToken: builder.recToken || null,
      index: builder.index || null,
      reviews: builder.reviews || null,
      isYotpo: builder.isYotpo || null,
      handle: builder.handle || null,
    };
    this.productTileNode = builder.productTileNode || undefined;
    this.productTileImageWrapperNode = undefined;
    this.productTileImageNode = undefined;
    this.productTileImageLinkNode = undefined;
    this.productTileTitleLinkNode = undefined;
    this.productTileQuickViewLinkNode = undefined;
    this.productTileInfoWrapperNode = undefined;

    this.init();
  }

  /**
   * Initialize the Product Tile instance
   * @returns {void} there is no return
   */
  init() {
    if (!this.productTileNode) return;

    // Create product tile image wrapper
    this.setProductTileImage();

    // Create Product Tile brief information Part 1 (Color Swatches)
    this.setColorSwatches();

    // Create Quick View button, title, badges, reviews, yotpo, and price
    // These aren't user-visible tasks or they don't represent a big impact in the elemnent. So, we can defer it
    setTimeout(() => {
      const productTileInfoPriceWrapper = document.createElement('div');
      this.setProductTileTitle(productTileInfoPriceWrapper);
      this.setPrice(productTileInfoPriceWrapper);
      this.setBadges(this.productTileImageWrapperNode);
      this.setQuickViewButton();
      if (this.productTileData.reviews) this.setReviews();
      if (this.productTileData.isYotpo) this.enableYotpo();
    }, 0);
  }

  /**
   * Build Product Tile image and set a first image
   */
  setProductTileImage() {
    const productTileImageWrapper = document.createElement('div');
    this.productTileImageWrapperNode = productTileImageWrapper;
    productTileImageWrapper.classList.add('ProductTile__image');
    productTileImageWrapper.setAttribute('data-product-title', this.productTileData.title);
    productTileImageWrapper.setAttribute('data-product-id', this.productTileData.pid);
    productTileImageWrapper.setAttribute('title', this.productTileData.title);
    productTileImageWrapper.setAttribute('data-product-tile-image', true);
    productTileImageWrapper.setAttribute('data-store-scroll-position', true);

    // Create product tile image link
    const productTileImageLink = document.createElement('a');
    productTileImageLink.href = this.productTileData.productUrl;
    productTileImageLink.setAttribute('target', '_self');
    this.productTileImageLinkNode = productTileImageLink;

    // Create product tile image element
    const productTileImage = document.createElement('img');
    this.productTileImageNode = productTileImage;
    productTileImage.classList.add('lazyload');
    productTileImage.src = this.setImage(this.productTileData.imageUrl);
    productTileImage.width = this.#imageWidth;
    productTileImage.height = this.#imageHeight;
    productTileImage.alt = this.productTileData.imageAlt;
    productTileImage.setAttribute('data-product-tile', this.productTileData.title);
    productTileImage.setAttribute('data-product-id', this.productTileData.pid);

    productTileImageLink.appendChild(productTileImage);
    productTileImageWrapper.appendChild(productTileImageLink);
    this.productTileNode.appendChild(productTileImageWrapper);
  }

  /**
   * Set as many color swatches as they are posible, sellable, and available
   */
  setColorSwatches() {
    const productTileInfoWrapper = document.createElement('div');
    this.productTileInfoWrapperNode = productTileInfoWrapper;
    productTileInfoWrapper.classList.add('ProductTile__info');
    const productTileColorSwatchesWrapper = document.createElement('div');
    productTileColorSwatchesWrapper.classList.add('ProductTile__color-swatch');
    const hasOneUniqueOption = this.productTileData.options.length === 1;

    this.productTileData.options.slice(0, 5).forEach((option, index) => {
      productTileColorSwatchesWrapper.appendChild(this.buildColorSwatch(option, index, hasOneUniqueOption));
    });

    if (this.productTileData.options.length > 5) {
      const restOfColors = document.createElement('span');
      restOfColors.classList.add('ProductTile__color-swatch-count');
      restOfColors.innerText = `+${this.productTileData.options.length - 5}`;
      productTileColorSwatchesWrapper.appendChild(restOfColors);
    }

    productTileInfoWrapper.appendChild(productTileColorSwatchesWrapper);
    this.productTileNode.appendChild(productTileInfoWrapper);
  }

  /**
   * Set an event on a specific color swatch. This will change image and different urls
   * employed inside of Product Tile
   * @param {MouseEvent} event
   * @returns - There is no return
   */
  switchColor(event) {
    if (!event) return;

    const currentTarget = event.currentTarget;
    const parentNode = event.currentTarget.parentNode;

    parentNode.childNodes.forEach((child) => child.classList.remove('active'));
    currentTarget.classList.add('active');

    this.productTileImageNode.src = this.setImage(
      this.productTileData.options.find((option) => option.Color.toLowerCase() === currentTarget.title.toLowerCase())
        .img,
    );
    this.productTileImageLinkNode.href = currentTarget.href;
    this.productTileTitleLinkNode.href = currentTarget.href;
    this.productTileQuickViewLinkNode.setAttribute('href', currentTarget.href);
    this.productTileQuickViewLinkNode.setAttribute('data-id', currentTarget.dataset.variantId);
  }

  /**
   * Provide other query params to image url to get a responsive image
   * @param {String} imageUrl - image url that come from source
   * @returns {String} image url with some query params
   */
  setImage(imageUrl) {
    return imageUrl
      .replace('_large', '')
      .replace('_600x', '')
      .replace('.jpg', `_${this.#imageWidth}x${this.#imageHeight}_crop_center.jpg`)
      .replace('.webp', `_${this.#imageWidth}x${this.#imageHeight}_crop_center.webp`);
  }

  /**
   * Build Quick View button
   */
  setQuickViewButton() {
    const productTileQuickViewButtonWrapper = document.createElement('div');
    productTileQuickViewButtonWrapper.classList.add('ProductTile__btn-quickviewWrapper');

    const productTileQuickViewButton = document.createElement('div');
    this.productTileQuickViewLinkNode = productTileQuickViewButton;

    productTileQuickViewButton.classList.add('ProductTile__btn-quickview');
    productTileQuickViewButton.setAttribute('data-quick-view-button', true);
    productTileQuickViewButton.setAttribute('href', this.productTileData.productUrl);
    productTileQuickViewButton.setAttribute('data-is-rec', true);
    productTileQuickViewButton.setAttribute('data-pid', this.productTileData.pid);
    productTileQuickViewButton.setAttribute('data-id', this.productTileData.options[0].variantId);
    productTileQuickViewButton.setAttribute('data-product-id', this.productTileData.pid);
    productTileQuickViewButton.setAttribute(
      'data-title',
      `${this.productTileData.title.replace('PLACEHOLDER - ', '').replace('CLEARANCE - ', '')}`,
    );
    productTileQuickViewButton.innerHTML = `
      <div
        class="ProductTile__gtm-click"
        data-pid=${this.productTileData.pid}
        data-id=${this.productTileData.options[0].variantId}
        data-product-id=${this.productTileData.pid}
        data-title="${this.productTileData.title.replace('PLACEHOLDER - ', '').replace('CLEARANCE - ', '')}"
      ></div>
      <i class="far fa-shopping-bag"></i>
      <span>&nbsp;</span>
      <span>Quick Shop</span>
    `;
    productTileQuickViewButtonWrapper.appendChild(productTileQuickViewButton);
    this.productTileImageWrapperNode.appendChild(productTileQuickViewButtonWrapper);
  }

  /**
   * Build the Product Tile title link
   * @param {HTMLElement} parentNode - parent element where ProductTile title will be appended
   */
  setProductTileTitle(parentNode) {
    parentNode.classList.add('ProductTile__info', 'ProductTile__info--price');
    const productTileTitleLink = document.createElement('a');
    this.productTileTitleLinkNode = productTileTitleLink;
    productTileTitleLink.classList.add('ProductTile__title');
    productTileTitleLink.href = this.productTileData.productUrl;
    productTileTitleLink.setAttribute('data-product-tile-title', true);
    productTileTitleLink.innerText = this.productTileData.title;

    const productTilePriceWrapper = document.createElement('div');
    productTilePriceWrapper.classList.add('ProductTile__price-row');
    const productTilePrice = document.createElement('p');
    productTilePrice.classList.add('ProductTile__price');
    productTilePrice.tabIndex = 0;

    parentNode.appendChild(productTileTitleLink);
    this.productTileNode.appendChild(parentNode);
  }

  /**
   * Build ProductTile prices based on price, compare_at_price, and currency
   * @param {HTMLElement} parentNode - parent element where ProductTile price will be appended
   */
  setPrice(parentNode) {
    const priceWrapper = document.createElement('p');
    priceWrapper.classList.add('ProductTile__price');
    priceWrapper.setAttribute('data-product-tile-price', true);
    priceWrapper.role = 'region';
    priceWrapper.tabIndex = 0;

    const template = document.body.getAttribute('data-template');
    const currency_alternate_iso =
      currency_symbol === '$' && currency_iso !== 'USD' && currency_iso !== 'HKD' ? currency_iso : '';

    // First try to find sellable variants, then fallback to any variant with price
    const sellableVariants = this.productTileData.variants.filter((variant) => variant.price && variant.Sellable);
    const variantsToUse =
      sellableVariants.length > 0 ? sellableVariants : this.productTileData.variants.filter((variant) => variant.price);

    variantsToUse.forEach((variant) => {
      this.#setMaximumAvailablePrice(variant.price);
      this.#setMinimumAvailablePrice(variant.price);
    });

    // Fallback to using the passed price if no variants processed
    if (this.#minimumAvailablePrice === 0 && this.productTileData.price) {
      this.#minimumAvailablePrice = this.productTileData.price;
      this.#maximumAvailablePrice = this.productTileData.price;
    }

    if (this.#minimumAvailablePrice !== this.#maximumAvailablePrice) {
      priceWrapper.ariaLabel = `Price: ${currency_alternate_iso} ${window.formatter.format(
        this.#minimumAvailablePrice,
      )} - ${currency_alternate_iso} ${window.formatter.format(this.#maximumAvailablePrice)}`;
      priceWrapper.innerHTML = `
        ${ProductTile.currencyIso !== 'USD' ? `<span>Starting at: </span>` : ''}
        <span class=${this.productTileData.maxCompareAtPrice > this.productTileData.price ? 'sale' : ''}>
          ${currency_alternate_iso}
          ${window.formatter.format(this.#minimumAvailablePrice)} - ${currency_alternate_iso}
          ${window.formatter.format(this.#maximumAvailablePrice)}
        </span>
        <br />
      `;
      parentNode.appendChild(priceWrapper);
    } else if (template === 'product' && this.productTileData.price === this.productTileData.compareAtPrice) {
      priceWrapper.ariaLabel = `Price: ${currency_alternate_iso} ${window.formatter.format(
        this.#minimumAvailablePrice,
      )}`;
      priceWrapper.innerHTML = `
        ${currency_alternate_iso}
        ${window.formatter.format(this.#minimumAvailablePrice)}
      `;
      parentNode.appendChild(priceWrapper);
    } else if (
      (template === 'product' && this.productTileData.price < this.productTileData.compareAtPrice) ||
      this.productTileData.maxCompareAtPrice > this.productTileData.price
    ) {
      const priceDiscountWrapper = document.createElement('div');
      priceDiscountWrapper.classList.add('ProductTile__price');
      priceDiscountWrapper.role = 'region';
      priceDiscountWrapper.tabIndex = 0;
      priceDiscountWrapper.ariaLabel = `Price: ${currency_alternate_iso} ${window.formatter.format(
        this.productTileData.price,
      )}, Before: ${currency_alternate_iso} ${window.formatter.format(
        this.productTileData.maxCompareAtPrice,
      )}, Discount: ${Math.round(
        ((this.productTileData.maxCompareAtPrice - this.productTileData.price) /
          this.productTileData.maxCompareAtPrice) *
          100,
      )}% off`;
      priceDiscountWrapper.setAttribute('data-product-tile-price', true);

      priceDiscountWrapper.innerHTML = `
        <div class="sale__container">
          <span class="sale">
            ${currency_alternate_iso}
            ${window.formatter.format(this.productTileData.price)}
          </span><span class="original">
            ${currency_alternate_iso}
            ${window.formatter.format(this.productTileData.maxCompareAtPrice)}
          </span>
        </div>
        <div class="dicount__container">
          <span class="discount">
            ${Math.round(
              ((this.productTileData.maxCompareAtPrice - this.productTileData.price) /
                this.productTileData.maxCompareAtPrice) *
                100,
            )}% off
          </span>
        </div>
      `;
      parentNode.appendChild(priceDiscountWrapper);
    } else {
      priceWrapper.ariaLabel = `Price: ${currency_alternate_iso} ${window.formatter.format(
        this.#minimumAvailablePrice,
      )}`;
      priceWrapper.innerHTML = `
        ${currency_alternate_iso}
        ${window.formatter.format(this.#minimumAvailablePrice)}
      `;
      parentNode.appendChild(priceWrapper);
    }
  }

  /**
   * Private method to set minimum price here, it will be stored in a private variable
   * @param {String} price
   */
  #setMinimumAvailablePrice(price) {
    if (
      this.#minimumAvailablePrice === 0 ||
      this.#minimumAvailablePrice > Number(parseFloat(price.replace(`${ProductTile.currencyIso}:`, '')).toFixed(2))
    ) {
      this.#minimumAvailablePrice = parseFloat(price.replace(`${ProductTile.currencyIso}:`, '')).toFixed(2);
    }
  }

  /**
   * Private method to set maximum price here, it will be stored in a private variable
   * @param {String} price
   */
  #setMaximumAvailablePrice(price) {
    if (
      this.#maximumAvailablePrice === 0 ||
      this.#maximumAvailablePrice < Number(parseFloat(price.replace(`${ProductTile.currencyIso}:`, '')).toFixed(2))
    ) {
      this.#maximumAvailablePrice = parseFloat(price.replace(`${ProductTile.currencyIso}:`, '')).toFixed(2);
    }
  }

  /**
   * Create a new ProductBadge instance on ProductTile based on product tags
   * @param {HTMLElement} parentNode - parent element where ProductTileBadge will be appended
   */
  setBadges(parentNode) {
    new ProductBadge(this.productTileData.tags, parentNode);
  }

  /**
   * Build a new url adding new query parameters based on variant id value
   * @param {String} variantId - product variant id value
   * @returns {String} - new url
   */
  buildUrlOption(variantId) {
    const baseUrl = this.productTileData.productUrl.split('?')[0];
    const rectToken = this.productTileData.productUrl.split('recToken=')[1];
    return `${baseUrl}?variant=${variantId}&recToken=${rectToken}`;
  }

  /**
   * Build just one color swatch instance and set up its color
   * @param {Object} option - Product Tile object item
   * @param {Number} index - color swatch's position
   * @returns Color Swatch instance
   */
  buildColorSwatch(option, index, hasOneUniqueOption = false) {
    const productTileColorSwatchLink = document.createElement('a');
    const swatch = this.productTileData.productType.indexOf('GIVEX') > -1 ? 'GCS' : 'SWT';
    const bgUrl = `url('https://cdn.shopify.com/s/files/1/0070/8853/7651/t/8/assets/${option.Barcode}_${swatch}_48x48_crop_center.jpg?v=${window.theme.swatches_updated_at}')`;

    productTileColorSwatchLink.classList.add(
      'ProductTile__color-swatch-item',
      'avail',
      `isp-product-color-swatch-${option.Color.replace(' ', '_').toLowerCase()}`,
    );
    if (index === 0 && !hasOneUniqueOption) productTileColorSwatchLink.classList.add('active');

    productTileColorSwatchLink.setAttribute('data-url-test', this.productTileData.productUrl);
    productTileColorSwatchLink.setAttribute('data-variant-id', option.variantId);
    productTileColorSwatchLink.setAttribute('target', '_self');
    productTileColorSwatchLink.href =
      index === 0 ? this.productTileData.productUrl : this.buildUrlOption(option.variantId);
    productTileColorSwatchLink.title = option.Color;
    productTileColorSwatchLink.role = 'button';
    productTileColorSwatchLink.setAttribute('aria-label', `Select ${option.Color} color`);
    productTileColorSwatchLink.style.backgroundImage = bgUrl;
    productTileColorSwatchLink.tabIndex = 0;

    productTileColorSwatchLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.switchColor(e);
    });

    return productTileColorSwatchLink;
  }

  /**
   * Build Product Tile Reviews
   */
  setReviews() {
    const reviewsInstance = new ProductReviews(this.productTileData.reviews);
    this.productTileInfoWrapperNode.appendChild(reviewsInstance.reviewsNode);
  }

  /**
   * Render Yotpo container
   */
  enableYotpo() {
    const yotpoElement = document.createElement('div');
    yotpoElement.classList.add('yotpo', 'bottomLine', 'yotpo-small');
    yotpoElement.setAttribute('data-appkey', ProductTile.yotpo_key);
    yotpoElement.setAttribute('data-domain', window.location.hostname);
    yotpoElement.setAttribute('data-product-id', this.productTileData.pid);
    yotpoElement.setAttribute('data-product-models', this.productTileData.pid);
    yotpoElement.setAttribute('data-name', this.productTileData.title);
    yotpoElement.setAttribute(
      'data-url',
      `https://${window.location.hostname}/products/${this.productTileData.handle}`,
    );
    yotpoElement.setAttribute('data-bread-crumbs', this.productTileData.tags);
    yotpoElement.setAttribute('data-description', '');
    yotpoElement.setAttribute('data-image-url', this.productTileData.imageUrl);
    this.productTileInfoWrapperNode.appendChild(yotpoElement);
  }
}
